import React, { useState } from 'react';
import MDBox from 'src/components/md/MDBox';
import {
  Card,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import MDTypography from 'src/components/md/MDTypography';
import MDInput from 'src/components/md/MDInput';
import useCustomers, {
  CustomerPreviewDTO,
} from 'src/api/accounts-receivable/customers/useCustomers';
import useOrganizationConfig from 'src/api/core/organization-config/useOrganizationConfig';
import useOrderfulEdiConfigs from 'src/api/erp-integrations/orderful-edi/useOrderfulEdiConfigs';
import useOrderfulEdiConfigValue from 'src/api/erp-integrations/orderful-edi/useOrderfulEdiConfigValue';

function OrderfulEdiConfigCard() {
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerPreviewDTO | null>(null);
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const { data: customersData, isLoading: isLoadingCustomers } = useCustomers(
    { name: customerSearchQuery },
    true,
  );

  const { data: orgConfigData } = useOrganizationConfig();
  const organizationName =
    orgConfigData?.organizationConfig?.organization?.name ?? 'Organization';

  const { data: ediConfigData, isLoading: isLoadingEdiConfigs } =
    useOrderfulEdiConfigs({
      type: isEnabled ? 'organization' : 'customer',
      customerId: selectedCustomer?.id,
    });

  const { data: configValue, isLoading: isLoadingValue } =
    useOrderfulEdiConfigValue({
      type: isEnabled ? 'organization' : 'customer',
      customerId: selectedCustomer?.id,
      key: selectedKey || undefined,
    });

  return (
    <Card sx={{ p: 3, height: '100%' }}>
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Top section with two lists */}
        <Grid item xs={12} sx={{ height: '50%' }}>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            {/* Organization Checkbox and Customer Search */}
            <Grid item xs={6}>
              <Paper
                elevation={2}
                sx={{ height: '100%', overflow: 'auto', p: 2 }}
              >
                <MDTypography variant="h6" mb={2}>
                  Organization
                </MDTypography>
                <MDBox mb={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isEnabled}
                        onChange={(e) => {
                          setIsEnabled(e.target.checked);
                          setSelectedKey(null);
                          if (e.target.checked) {
                            setSelectedCustomer(null);
                            setCustomerInputValue('');
                            setCustomerSearchQuery('');
                          }
                        }}
                      />
                    }
                    label={
                      <MDTypography variant="button" fontWeight="regular">
                        {organizationName}
                      </MDTypography>
                    }
                  />
                </MDBox>
                <MDTypography variant="h6" mb={2}>
                  Customers
                </MDTypography>
                <Autocomplete
                  value={selectedCustomer}
                  onChange={(_, newValue) => {
                    setSelectedCustomer(newValue);
                    setSelectedKey(null);
                  }}
                  inputValue={customerInputValue}
                  onInputChange={(_, newInputValue) => {
                    setCustomerInputValue(newInputValue);
                    setCustomerSearchQuery(newInputValue);
                  }}
                  options={customersData?.edges || []}
                  getOptionLabel={(option) => option.name}
                  loading={isLoadingCustomers}
                  disabled={isEnabled}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      label="Search customers"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingCustomers ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <MDTypography variant="button">
                        {option.name}
                      </MDTypography>
                    </li>
                  )}
                />
                <MDBox mt={2}>
                  <MDTypography variant="button" fontWeight="bold">
                    {isEnabled
                      ? 'Selected Organization:'
                      : 'Selected Customer:'}
                  </MDTypography>
                  <MDTypography variant="button" display="block">
                    {isEnabled
                      ? organizationName
                      : selectedCustomer?.name || 'None selected'}
                  </MDTypography>
                </MDBox>
              </Paper>
            </Grid>
            {/* Key List */}
            <Grid item xs={6}>
              <Paper elevation={2} sx={{ height: '100%', overflow: 'auto' }}>
                <MDTypography variant="h6" p={2}>
                  Keys
                </MDTypography>
                <List>
                  {isLoadingEdiConfigs ? (
                    <ListItem>
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        width="100%"
                        py={2}
                      >
                        <CircularProgress size={24} />
                      </MDBox>
                    </ListItem>
                  ) : ediConfigData?.configs?.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary={
                          <MDTypography variant="button" color="text">
                            No keys found
                          </MDTypography>
                        }
                      />
                    </ListItem>
                  ) : (
                    ediConfigData?.configs?.map(
                      (config: { id: string; key: string }) => (
                        <ListItemButton
                          key={config.id}
                          sx={{ px: 2 }}
                          selected={selectedKey === config.key}
                          onClick={() => setSelectedKey(config.key)}
                        >
                          <ListItemText
                            primary={
                              <MDBox
                                sx={{
                                  display: 'block',
                                  width: '100%',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  px: 1.5,
                                  py: 0.5,
                                  ml: -1, // Compensate for ListItemText's default padding
                                }}
                              >
                                <MDTypography
                                  sx={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontSize: 'inherit',
                                    fontWeight: 'inherit',
                                    color: 'inherit',
                                  }}
                                  variant="button"
                                >
                                  {config.key}
                                </MDTypography>
                              </MDBox>
                            }
                          />
                        </ListItemButton>
                      ),
                    )
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Bottom section for value */}
        <Grid item xs={12} sx={{ height: '50%' }}>
          <Paper elevation={2} sx={{ height: '100%', p: 2 }}>
            <MDTypography variant="h6" mb={2}>
              Value
            </MDTypography>
            <MDBox sx={{ height: 'calc(100% - 40px)', overflow: 'auto' }}>
              {isLoadingValue ? (
                <MDBox display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </MDBox>
              ) : !selectedKey ? (
                <MDTypography variant="body2" color="text">
                  Select a key to view its value
                </MDTypography>
              ) : configValue ? (
                <>
                  <MDBox
                    component="pre"
                    sx={{
                      margin: 0,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      mb: 2,
                    }}
                  >
                    {JSON.stringify(configValue.value, null, 2)}
                  </MDBox>
                  <MDBox sx={{ textAlign: 'right' }}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                    >
                      Created:{' '}
                      {new Date(configValue.createdAt).toLocaleString()}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                    >
                      Updated:{' '}
                      {new Date(configValue.updatedAt).toLocaleString()}
                    </MDTypography>
                  </MDBox>
                </>
              ) : (
                <MDTypography variant="body2" color="error">
                  Failed to load value
                </MDTypography>
              )}
            </MDBox>
          </Paper>
        </Grid>
      </Grid>
    </Card>
  );
}

export default OrderfulEdiConfigCard;
